import { del, get, patch, post, put } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { useCallback } from "react";

/**
 * Normalized error thrown by every call made through this hook.
 *
 * `statusCode` and `body` are lifted from the unwrapped HTTP response that
 * Amplify attaches to the errors its REST handlers throw. That response is
 * shaped `{ statusCode: number; headers: Record<string, string>; body?: string }`
 * — it carries no `status` field — and `body` is the response body as raw
 * text, so a caller wanting the server's own message parses it out of `body`
 * itself.
 *
 * These fields are redeclared here rather than imported from Amplify: the
 * response type is only reachable through a non-public entry point, so
 * depending on it would break on a patch bump. Only field names Amplify
 * documents as part of the thrown error are read.
 */
export interface ApiError {
  message: string;
  code?: string;
  statusCode?: number;
  /** Raw response body text, absent when the failure carried no response. */
  body?: string;
}

export interface UseApiProps {
  isAdmin?: boolean;
}

export const useApi = ({ isAdmin = false }: UseApiProps = {}) => {
  const handleApiCall = useCallback(
    async <T>(apiCall: () => Promise<T>): Promise<T | null> => {
      try {
        const result = await apiCall();
        return result;
      } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
        const apiError: ApiError = {
          message: err.message || "An error occurred",
          code: err.code,
          statusCode: err.response?.statusCode,
          body: err.response?.body,
        };
        throw apiError;
      }
    },
    []
  );

  const getAuthHeaders = useCallback(async () => {
    try {
      const { tokens } = await fetchAuthSession();
      // If the api is for admins, send the access token (for the authorizer).
      // Otherwise, send the ID token.
      return {
        Authorization: `Bearer ${
          isAdmin
            ? tokens?.accessToken?.toString()
            : tokens?.idToken?.toString()
        }`,
      };
    } catch (error) {
      console.warn("Failed to get auth headers:", error);
      return {};
    }
  }, [isAdmin]);

  const apiGet = useCallback(
    async <T>(path: string, options?: any): Promise<T | null> => { // eslint-disable-line @typescript-eslint/no-explicit-any
      return handleApiCall(async () => {
        const headers = await getAuthHeaders();
        const operation = get({
          apiName: "api",
          path,
          options: {
            headers,
            ...options,
          },
        });
        const { body } = await operation.response;
        return (await body.json()) as T;
      });
    },
    [handleApiCall, getAuthHeaders]
  );

  const apiPost = useCallback(
    async <T>(path: string, body?: any, options?: any): Promise<T | null> => { // eslint-disable-line @typescript-eslint/no-explicit-any
      return handleApiCall(async () => {
        const headers = await getAuthHeaders();
        const operation = post({
          apiName: "api",
          path,
          options: {
            body,
            headers,
            ...options,
          },
        });
        const { body: responseBody } = await operation.response;
        return (await responseBody.json()) as T;
      });
    },
    [handleApiCall, getAuthHeaders]
  );

  const apiPatch = useCallback(
    async <T>(path: string, body?: any, options?: any): Promise<T | null> => { // eslint-disable-line @typescript-eslint/no-explicit-any
      return handleApiCall(async () => {
        const headers = await getAuthHeaders();
        const operation = patch({
          apiName: "api",
          path,
          options: {
            body,
            headers,
            ...options,
          },
        });
        const { body: responseBody } = await operation.response;
        return (await responseBody.json()) as T;
      });
    },
    [handleApiCall, getAuthHeaders]
  );

  const apiPut = useCallback(
    async <T>(path: string, body?: any, options?: any): Promise<T | null> => { // eslint-disable-line @typescript-eslint/no-explicit-any
      return handleApiCall(async () => {
        const headers = await getAuthHeaders();
        const operation = put({
          apiName: "api",
          path,
          options: {
            body,
            headers,
            ...options,
          },
        });
        const { body: responseBody } = await operation.response;
        return (await responseBody.json()) as T;
      });
    },
    [handleApiCall, getAuthHeaders]
  );

  const apiDelete = useCallback(
    async <T>(path: string, options?: any): Promise<T | null> => { // eslint-disable-line @typescript-eslint/no-explicit-any
      return handleApiCall(async () => {
        const headers = await getAuthHeaders();
        const operation = del({
          apiName: "api",
          path,
          options: {
            headers,
            ...options,
          },
        });
        const { body } = await operation.response;
        return (await body.json()) as T;
      });
    },
    [handleApiCall, getAuthHeaders]
  );

  return {
    apiGet,
    apiPost,
    apiPatch,
    apiPut,
    apiDelete,
  };
};
